import firebase from "../../firebase"
import { TypeArray, BlockState, FirestoreTimestampType } from "../types"
import { reducerTypeGuard } from "../reducers/reducerGuards"
import { actionCreatorTypeGuard } from "../reducers/actionCreators"
import {
  processActionMeta,
  getArrayTypeReducerGuard
} from "../reducers"
import { BlockStateSchema } from "../schemas"

import _ from "lodash"

export type ScreenState = {
  leftState: Array<BlockState | null>
  rightState: Array<BlockState | null>
  screenId: string
  completed: boolean
  completed_timestamp: FirestoreTimestampType | null
}

const reducerAction = {
  leftState: actionCreatorTypeGuard<ScreenState, Array<BlockState | null>>(
    "leftState",
    "ScreenState"
  ),
  rightState: actionCreatorTypeGuard<ScreenState, Array<BlockState | null>>(
    "rightState",
    "ScreenState"
  ),
  screenId: actionCreatorTypeGuard<ScreenState, string>(
    "screenId",
    "ScreenState"
  ),
  completed: actionCreatorTypeGuard<ScreenState, boolean>(
    "completed",
    "ScreenState"
  ),
  completed_timestamp: actionCreatorTypeGuard<
    ScreenState,
    FirestoreTimestampType | null
  >("completed_timestamp", "ScreenState"),
  __record: actionCreatorTypeGuard<TypeArray, ScreenState>(
    "ScreenState",
    "__record"
  ),
  __create: actionCreatorTypeGuard<TypeArray, null | Partial<ScreenState>>(
    "create",
    "__create/ScreenState"
  ),
  __delete: actionCreatorTypeGuard<TypeArray, number>("delete", "__record")
}

// Type safety to ensure we have a defined action for each property
type ReducerActions = {
  [key in
    | keyof ScreenState
    | "__record"
    | "__delete"
    | "__create"]: (typeof reducerAction)[key][0]
}

// Our exported actions
export const screenStateAction = Object.fromEntries(
  Object.entries(reducerAction).map(k => [k[0], k[1][0]])
) as ReducerActions

const validationOptions = {
  strict: true,
  abortEarly: false,
  stripUnknown: true,
  recursive: true
}

export const screenStateReducer = reducerTypeGuard<ScreenState>()
  .case(...reducerAction.screenId)
  .case(screenStateAction.completed, (state, completed) => ({
    ...state,
    completed: completed,
    completed_timestamp: firebase.firestore.Timestamp.now()
  }))
  .case(...reducerAction.completed_timestamp)
  .default((state, action: any) => {
    const prevState = _.cloneDeep(state)
    action = processActionMeta(action)

    const {
      prop,
      idx,
      nodes
    }: { prop: keyof ScreenState; idx: number; nodes: Array<any> } =
      action?.meta
    if (prop == "leftState" || prop == "rightState") {
      if (prevState?.[prop]?.length < idx + 1) {
        prevState[prop][idx] = BlockStateSchema.cast({}, validationOptions)
      }
    }

    switch (action?.meta?.prop) {
      case "leftState":
        return getArrayTypeReducerGuard<ScreenState, BlockState | null>(
          "ScreenState",
          "leftState",
          "BlockState",
          prevState,
          action
        )
      case "rightState":
        return getArrayTypeReducerGuard<ScreenState, BlockState | null>(
          "ScreenState",
          "rightState",
          "BlockState",
          prevState,
          action
        )
    }

    return prevState
  })
